import { ApiFile } from './File'
import { ApiIncidentCaseworker, ApiOrganization, ApiUser } from './User'

export interface ApiIncidentQuestionAnswer {
    id: number
    question: string | null
    answer: string
}

export interface ApiIncidentForward {
    severity_score: number
    comment: string
    created_at: string
    forwarded_to?: string
    forwarded_by?: string
}

export interface ApiIncidentRecording {
    transcription: string | null
    url: string
}

export interface ApiIncidentDeletionDate {
    days: number | string
    hours: number | string
}

export interface ApiIncidentReporter {
    first_name: string
    last_name: string
    email: string
    phone_number: string
    position: string
    country_id: number
}

export interface ApiIncident {
    id: number
    uuid: string
    readable_id: string
    title: string
    description: string
    case_type: string
    status: string
    closed_comment: string | null
    closed_at: string | null
    updated_at: string
    created_at: string
    whistleblower_code: string | null
    category: string | null
    category_id: number | null
    forwarded_at: string | null
    preferred_contact: ApiUser | null
    caseworkers: { data: ApiIncidentCaseworker[] }
    excluded_caseworkers: { data: ApiIncidentCaseworker[] }
    recording: ApiIncidentRecording | null
    deletion_date: ApiIncidentDeletionDate | null
    forward?: { data: ApiIncidentForward } | null
    questions?: { data: ApiIncidentQuestionAnswer[] }
    files?: { data: ApiFile[] }
    assigned?: { data: ApiUser }
    organization?: { data: ApiOrganization }
    talk?: { data: { id: string } }
    reporter?: { data: ApiIncidentReporter }
    logs?: { data: ApiIncidentLog[] }
}

export interface ApiIncidentLog {
    id: number
    label: string
    title: string
    message: string
    attributes: Record<string, string>
    time_diff: string
    created_at: string
}

/**
 * Shape returned by the backend DecryptReportTrait::decryptOne()
 * plus extra fields added by IncidentsService::show().
 * Used for the whistleblower's view of their own report.
 */
export interface ApiWhistleblowerReport {
    id: number
    title: string
    description: string
    uuid: string
    category: string | null
    assigned_to: Array<{
        id: number
        name: string
        profile_img: string | null
        note: string | null
        position: string | null
        languages: Array<{ id: number; flag: string; name: string }>
    }>
    organization_name: string
    case_type: string
    status: string
    created_at: string
    closed_comment: string | null
    closed_at: string | null
    talk_id: string | null
    voice_recording?: string
    whistleblower_code: string
    files: Array<{
        id: number
        name: string
        file_path: string
        file_size: number
        extension: string
    }>
    reporter: {
        name: string | null
        email: string | null
        phone: string | null
    } | null
    forwarded_at?: string
}

export interface ApiIncidentPreview {
    id: number
    title: string
    status: string
    uuid: string
    organizationName: string
    caseworkerCount: number
    deletedAt: string | null
    readableId: string
}

/**
 * Normalized list-item used in Redux state and UI.
 * Extends the preview shape with fields the filter/UI needs
 * that only the full IncidentTransformer provides.
 */
export interface ReportListItem extends ApiIncidentPreview {
    assignedToName: string | null
    assignedToId: number | null
    category: string | null
}

/** Map a full ApiIncident (from IncidentTransformer) to the flat list shape. */
export const mapIncidentToListItem = (
    incident: ApiIncident
): ReportListItem => {
    const assigned = incident.assigned?.data
    return {
        id: incident.id,
        title: incident.title,
        status: incident.status,
        uuid: incident.uuid,
        organizationName: incident.organization?.data?.name ?? '',
        caseworkerCount: incident.caseworkers?.data?.length ?? 0,
        deletedAt: null,
        readableId: incident.readable_id,
        assignedToName: assigned
            ? `${assigned.first_name} ${assigned.last_name}`
            : null,
        assignedToId: assigned?.id ?? null,
        category: incident.category,
    }
}

/** Map a preview (from PreviewIncidentTransformer) to the list shape. */
export const mapPreviewToListItem = (
    preview: ApiIncidentPreview
): ReportListItem => ({
    ...preview,
    assignedToName: null,
    assignedToId: null,
    category: null,
})
